import { Button, Col, Row } from "reactstrap";
import { useRef, useState, useCallback } from "react";
import "react-circular-progressbar/dist/styles.css";
import { useNavigate } from "react-router-dom";
import RotatePhonePopup from "../RotatePhonePopup";
import { useLanguage } from "../context/Languagecontext";
import { useTranslatedText } from "../../hooks/useTranslatedText";

// ─── Gradient theme ───────────────────────────────────────────────
const IMPRESSION_GRAD = ["#FFB244", "#FF408B"];

const makeGradient = (grad, direction = "135deg") => {
    if (!grad || grad[0] === grad[1]) return grad[0];
    return `linear-gradient(${direction}, ${grad[0]}, ${grad[1]})`;
};

const gradientTextStyle = (grad) => ({
    background: makeGradient(grad, "135deg"),
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
});



// ─── Gradient Radio Icon ──────────────────────────────────────────
let _gradCounter = 0;
const RadioIcon = ({ grad, isSelected }) => {
    const gradientId = useRef(`cat13-grad-${++_gradCounter}`).current;
    const isSingle = !grad || grad[0] === grad[1];
    const gradColor = isSingle ? grad[0] : `url(#${gradientId})`;
    return (
        <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            {isSelected && !isSingle && (
                <defs>
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={grad[0]} />
                        <stop offset="100%" stopColor={grad[1]} />
                    </linearGradient>
                </defs>
            )}
            <circle cx="10" cy="10" r="8.5"
                stroke={isSelected ? gradColor : "rgba(0,0,0,1)"}
                strokeWidth="1.8" fill="none" />
            {isSelected && <circle cx="10" cy="10" r="4.5" fill={gradColor} />}
        </svg>
    );
};

// ─── Avatar placeholder ───────────────────────────────────────────
const AvatarPlaceholder = ({ label }) => (
    <div style={{
        width: 38, height: 38, borderRadius: "50%",
        background: "linear-gradient(135deg, #e0e0e0, #c8c8c8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 600, color: "#888", flexShrink: 0,
    }}>
        {label?.[0] || "?"}
    </div>
);


const Category13 = ({ hint, uploadedImage, originalData, username, category }) => {
    const { lanText, t } = useLanguage();
    const navigate = useNavigate();

    const isSmallScreen = window.innerWidth < 380 && window.innerWidth !== 360;
    const [selectedId, setSelectedId] = useState(null);

    const data = originalData.question;
    const questionText = Array.isArray(data)
        ? data.find(item => item.category === category)?.question || t.emotion.defaultQuestion
        : t.emotion.defaultQuestion;
    const translatedQuestion = useTranslatedText(questionText, lanText);
    const grad = IMPRESSION_GRAD;


    const optionList = [
        { id: "A", label: t?.reputation?.low || "Low", avatar: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-8142d87b-e222-4d8c-934f-15408c477442.png" },
        { id: "B", label: t?.reputation?.average || "Average", avatar: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-645ae386-7e1b-40fe-a33c-2c1490a7e7eb.png" },
        { id: "C", label: t?.reputation?.good || "Good", avatar: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-ee5371b9-d676-4ee6-abc2-7ad1985a3b2b.png" },
        { id: "D", label: t?.reputation?.high || "High", avatar: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-09b3e102-8a4c-4894-b97d-576fc39a368f.png" },
        { id: "E", label: t?.reputation?.veryhigh || "Very High", avatar: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-eb06cc35-be34-46c6-8964-b161b73dd9ac.png" },
    ];

    const navigateToNextScreen = useCallback(() => {
        const formData = {
            originalData,
            hint,
            uploadedImage,
            translatedQuestion,
            selectedOption: selectedId,
        };
        sessionStorage.setItem('impressionFlowData', JSON.stringify(formData));
        navigate(`/${username || ''}/${category || ''}/impressioncard`, {
            state: formData,
            replace: false
        });
    }, [originalData, hint, uploadedImage, translatedQuestion, selectedId, username, category, navigate]);


    return (
        <div className='GTRegular' style={{ height: '100dvh', background: "linear-gradient(145deg, #FFB244, #FF408B)" }}>
            <div className='h-100 pt-42'>
                <Row className={`h-100`}>
                    <Col className="p-0 d-flex flex-column align-items-between justify-content-between" style={{ height: '100%' }}>

                        {/* ── Top text ── */}
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '12%', padding: '20px 0' }}>
                            <div className="text-center text-black" style={{ maxWidth: "90vw" }}>
                                <p className="lh-sm mx-auto px-3 m-0 mb-3 mt-1"
                                    style={{ fontSize: "17px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {t.emotion.text} <br /> {t.emotion.text2}
                                </p>
                            </div>
                        </div>

                        {/* ── Middle: white card with question + options ── */}
                        <div className="d-flex justify-content-center align-items-center" style={{ flex: "1 0 auto", maxHeight: "550px", padding: "0 16px" }}>
                            <div className="d-flex flex-column align-items-start h-100 bg-white position-relative overflow-hidden"
                                style={{ width: "450px", maxWidth: "88vw", borderRadius: "15px", boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.25)" }}>

                                {/* Black question bar */}
                                <div className="bg-black w-100 text-center text-white d-flex align-items-center justify-content-center px-4 py-3 GTBold"
                                    style={{
                                        minHeight: "80px",
                                        maxHeight: "120px",
                                        flexShrink: 0,
                                        fontSize:
                                            translatedQuestion.length > 100 ? "10px"
                                                : translatedQuestion.length > 80 ? "11px"
                                                    : translatedQuestion.length > 60 ? "12px"
                                                        : translatedQuestion.length > 40 ? "14px"
                                                            : "16px",
                                    }}>
                                    <p className="m-0 text-white">
                                        {t?.common?.que || "Que:"} {translatedQuestion}
                                    </p>
                                </div>

                                {/* ── Options list — fills remaining height equally ── */}
                                <div className="p-3" style={{
                                    flex: 1,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: "8px 12px",
                                    gap: isSmallScreen ? "10px" : "13px",
                                    minHeight: 0,
                                    overflow: "hidden",
                                }}>
                                    {optionList.map((opt) => {
                                        const isSelected = selectedId === opt.id;
                                        return (
                                            <div className="p-3"
                                                key={opt.id}
                                                onClick={() => setSelectedId(opt.id)}
                                                style={{
                                                    flex: "1 1 0",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "12px",
                                                    borderRadius: "16px",
                                                    cursor: "pointer",
                                                    border: `1.5px solid #FF936C`,
                                                    background: "#FEEBE4",
                                                    transition: "all 0.18s ease",
                                                    minHeight: 0,
                                                }}
                                            >
                                                {/* Letter */}
                                                <span className="GTBold text-center" style={{
                                                    width: 22,
                                                    fontSize: 16,
                                                    flexShrink: 0,
                                                    ...(isSelected ? gradientTextStyle(grad) : { color: "#000" }),
                                                }}>
                                                    {opt.id}
                                                </span>

                                                {/* Avatar */}
                                                {opt.avatar
                                                    ? <img src={opt.avatar} alt={opt.label}
                                                        style={{ width: 35, height: 35, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                    : <AvatarPlaceholder label={opt.label} />
                                                }

                                                {/* Name */}
                                                <span className="GTMedium" style={{
                                                    flex: 1,
                                                    fontSize: 16,
                                                    transition: "color 0.15s ease",
                                                    ...(isSelected ? gradientTextStyle(grad) : { color: "#000" }),
                                                }}>
                                                    {opt.label}
                                                </span>

                                                {/* Radio */}
                                                <RadioIcon grad={grad} isSelected={isSelected} />
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                        </div>

                        {/* ── Next button ── */}
                        <div className="d-flex justify-content-center align-items-center px-3 mx-auto" style={{ height: '15dvh', width: "450px", maxWidth: "88vw" }}>
                            <Button
                                onClick={selectedId ? navigateToNextScreen : undefined}
                                className="rounded-pill mx-auto GTMedium text-white border-0"
                                style={{
                                    width: "100%",
                                    padding: "14px 1px",
                                    height: "56px",
                                    background: selectedId ? "black" : "rgba(0,0,0,0.35)",
                                    opacity: selectedId ? 1 : 0.6,
                                    cursor: selectedId ? "pointer" : "not-allowed",
                                    transform: selectedId ? "scale(0.95)" : "scale(0.92)",
                                    pointerEvents: selectedId ? "auto" : "none",
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
                                }}
                            >
                                <span className='GTMedium' style={{ fontSize: "clamp(21px, 4.5vw, 27px)", lineHeight: 1, whiteSpace: "nowrap" }}>
                                    {t?.common?.next || "Next"}
                                </span>
                            </Button>
                        </div>

                    </Col>
                </Row>
            </div>

            <RotatePhonePopup excludeWhen={[]} />
        </div>
    );
};

export default Category13;